import axios from 'axios';
import { trackConversationStarted, trackEventCreated } from './analytics';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Sin interceptor de login: rutas públicas con su propio Bearer (p. ej. repertorio compartido). */
const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir el token de autorización
api.interceptors.request.use(
  (config) => {
    // El cliente define `Content-Type: application/json` por defecto; con FormData hay que
    // quitarlo para que el navegador envíe `multipart/form-data` con boundary (si no, Multer no recibe `file`).
    if (config.data instanceof FormData && config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
        config.headers.delete('content-type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log temporal para debug
    if (config.url?.includes('/bandas') && config.params) {
      console.log('🔧 Axios config.params:', config.params);
      console.log('🔧 Axios URL completa:', config.url, config.baseURL);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar respuestas y refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Errores de plan (bloqueo por presupuesto o feature no incluida):
    // emitimos un evento global para que la UI muestre el pop-up correspondiente.
    const planCode = error.response?.data?.code;
    if (
      error.response?.status === 403 &&
      (planCode === 'PLAN_LIMIT_EXCEEDED' ||
        planCode === 'FEATURE_NOT_IN_PLAN' ||
        planCode === 'PLAN_QUOTA_EXCEEDED')
    ) {
      try {
        window.dispatchEvent(
          new CustomEvent('cadencya:plan-error', {
            detail: { code: planCode, data: error.response.data },
          }),
        );
      } catch { /* noop */ }
    }

    // Si el error es 401 y no es un retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Si falla el refresh, limpiar tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        // Usar navigate en vez de hard redirect para evitar reload completo
        // Solo redirigir si no estamos ya en login/registro
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/registro')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  getMe: () => api.get('/auth/me'),
  deactivateAccount: (password) => api.post('/auth/deactivate', { password }),
  exportUserData: () => api.get('/auth/export-data'),
  deleteAccountPermanently: (password) => api.post('/auth/delete-permanently', { password }),
  changeUsername: (username) => api.patch('/auth/username', { username }),
  // Suscripción / plan
  getPlan: () => api.get('/auth/plan'),
  upgradePlan: () => api.post('/auth/plan/upgrade'),
};

// Bandas API
export const bandasAPI = {
  getAll: (params) => api.get('/bandas', { params }),
  getById: (id) => api.get(`/bandas/${id}`),
  getStats: (id) => api.get(`/bandas/${id}/stats`),
  getMyProfile: () => api.get('/bandas/me/profile'),
  listColaboradoresGestion: () => api.get('/bandas/me/colaboradores'),
  createColaboradorGestion: (data) => api.post('/bandas/me/colaboradores', data),
  updateColaboradorGestion: (colaboradorUserId, data) =>
    api.patch(`/bandas/me/colaboradores/${encodeURIComponent(colaboradorUserId)}`, data),
  /** Años elegibles para filtros de métricas (alineado con reglas de rango en servidor). */
  getMetricasOpciones: () => api.get('/bandas/me/metricas/opciones'),
  /** Fase 1 métricas: rango resuelto (query: modo, anio, mes, anioMes, desde, hasta) */
  getMetricasRango: (params) => api.get('/bandas/me/metricas/rango', { params }),
  /** Fase 2+: rango + agregados; Fase 6 `narrativa`; Fase 7 pulido, caché back y tasa encuestas en resumen. */
  getMetricasResumen: (params) => api.get('/bandas/me/metricas/resumen', { params }),
  /** JWT para abrir /metricas/resumen?t=… (página pública, recalcula al visitar). */
  postMetricasShareLink: (body) => api.post('/bandas/me/metricas/share-link', body),
  // Objetivos (compromiso / asistencia)
  listObjetivos: () => api.get('/bandas/me/objetivos'),
  createObjetivo: (data) => api.post('/bandas/me/objetivos', data),
  getObjetivo: (id, params = {}) => api.get(`/bandas/me/objetivos/${encodeURIComponent(id)}`, { params }),
  patchObjetivo: (id, data) => api.patch(`/bandas/me/objetivos/${encodeURIComponent(id)}`, data),
  deleteObjetivo: (id) => api.delete(`/bandas/me/objetivos/${encodeURIComponent(id)}`),
  getRepertorioShare: () => api.get('/bandas/me/repertorio-share'),
  setRepertorioSharePassword: (data) => api.post('/bandas/me/repertorio-share/password', data),
  updateRepertorioSharePassword: (data) => api.patch('/bandas/me/repertorio-share/password', data),
  revealRepertorioSharePassword: (data) =>
    api.post('/bandas/me/repertorio-share/reveal-password', data),
  getRepertorioShareMusicoEnlaces: () => api.get('/bandas/me/repertorio-share/musico-enlaces'),
  registrarRepertorioShareMusicoCompartido: (musicoId) =>
    api.post(`/bandas/me/repertorio-share/musico-enlaces/${encodeURIComponent(musicoId)}/registrar-compartido`),
  listRepertorioShareIncidencias: () => api.get('/bandas/me/repertorio-share/incidencias'),
  patchRepertorioShareIncidencia: (incidenciaId, data) =>
    api.patch(`/bandas/me/repertorio-share/incidencias/${encodeURIComponent(incidenciaId)}`, data),
  listDocumentosGestion: () => api.get('/bandas/me/documentos-gestion'),
  uploadDocumentoGestion: (file, { titulo, categoria, descripcion, publicadoEnTablon }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('titulo', titulo);
    formData.append('categoria', categoria);
    if (descripcion != null && String(descripcion).trim() !== '') {
      formData.append('descripcion', String(descripcion).trim());
    }
    formData.append('publicadoEnTablon', publicadoEnTablon === true ? 'true' : 'false');
    return api.post('/bandas/me/documentos-gestion', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  patchDocumentoGestion: (documentoId, data) =>
    api.patch(`/bandas/me/documentos-gestion/${encodeURIComponent(documentoId)}`, data),
  deleteDocumentoGestion: (documentoId) =>
    api.delete(`/bandas/me/documentos-gestion/${encodeURIComponent(documentoId)}`),
  /** Blob del archivo (Bearer); para abrir en pestaña o guardar en el cliente. */
  fetchDocumentoGestionArchivoBlob: async (documentoId) => {
    const res = await api.get(
      `/bandas/me/documentos-gestion/${encodeURIComponent(documentoId)}/archivo`,
      { responseType: 'blob' },
    );
    const mime =
      res.headers['content-type']?.split(';')[0]?.trim() || 'application/octet-stream';
    return new Blob([res.data], { type: mime });
  },
  getDocumentosGestionShare: () => api.get('/bandas/me/documentos-gestion/share'),
  setDocumentosGestionSharePassword: (data) =>
    api.post('/bandas/me/documentos-gestion/share/password', data),
  updateDocumentosGestionSharePassword: (data) =>
    api.patch('/bandas/me/documentos-gestion/share/password', data),
  revealDocumentosGestionSharePassword: (data) =>
    api.post('/bandas/me/documentos-gestion/share/reveal-password', data),
  update: (data) => api.patch('/bandas/me', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bandas/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMyAvatarFile: () => api.get('/bandas/me/avatar-file', { responseType: 'blob' }),
  uploadFondo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bandas/me/fondo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadImagenCabeceraWeb: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bandas/me/imagen-cabecera-web', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteImagenCabeceraWeb: () => api.delete('/bandas/me/imagen-cabecera-web'),
  uploadPhotos: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/bandas/me/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (photoUrl) => api.delete('/bandas/me/photos', { data: { photoUrl } }),
  // Ensayos
  createEnsayo: (data) => api.post('/bandas/me/ensayos', data),
  updateEnsayo: (ensayoId, data) => api.patch(`/bandas/me/ensayos/${ensayoId}`, data),
  deleteEnsayo: (ensayoId) => api.delete(`/bandas/me/ensayos/${ensayoId}`),
  // Repertorio
  addToRepertorio: (data) => api.post('/bandas/me/repertorio', data),
  removeFromRepertorio: (repertorioId) => api.delete(`/bandas/me/repertorio/${repertorioId}`),
  scanRepertorioPdf: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bandas/me/repertorio/scan-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minutos para PDFs escaneados que usan visión
    });
  },
  // Obras con partituras (PDF/imagen por página)
  listObrasPartituras: () => api.get('/bandas/me/obras-partituras'),
  getObrasPartiturasEstadisticas: () => api.get('/bandas/me/obras-partituras/estadisticas'),
  listObrasPartiturasPaginas: () => api.get('/bandas/me/obras-partituras/paginas'),
  listObraPartituraGeneros: () => api.get('/bandas/me/obras-partituras/generos'),
  listPartiturasVoces: () => api.get('/bandas/me/obras-partituras/voces'),
  createPartituraVoz: (data) => api.post('/bandas/me/obras-partituras/voces', data),
  deletePartituraVoz: (id) => api.delete(`/bandas/me/obras-partituras/voces/${id}`),
  listPartiturasCarpetas: () => api.get('/bandas/me/obras-partituras/carpetas'),
  createPartituraCarpeta: (data) => api.post('/bandas/me/obras-partituras/carpetas', data),
  updatePartituraCarpeta: (carpetaId, data) =>
    api.patch(`/bandas/me/obras-partituras/carpetas/${carpetaId}`, data),
  deletePartituraCarpeta: (carpetaId) =>
    api.delete(`/bandas/me/obras-partituras/carpetas/${carpetaId}`),
  setPartituraCarpetaObras: (carpetaId, data) =>
    api.patch(`/bandas/me/obras-partituras/carpetas/${carpetaId}/obras`, data),
  previewPartichellasVoces: (data) =>
    api.post('/bandas/me/obras-partituras/partichellas/preview', data),
  downloadPartichellasPdf: (data) =>
    api.post('/bandas/me/obras-partituras/partichellas/pdf', data, { responseType: 'blob' }),
  createObraPartitura: (data) => api.post('/bandas/me/obras-partituras', data),
  getObraPartitura: (obraId) => api.get(`/bandas/me/obras-partituras/${obraId}`),
  listObraPartituraProgramaciones: (obraId) =>
    api.get(`/bandas/me/obras-partituras/${obraId}/programaciones`),
  updateObraPartitura: (obraId, data) => api.patch(`/bandas/me/obras-partituras/${obraId}`, data),
  uploadObraPartituraArchivos: (obraId, files) => {
    const formData = new FormData();
    // Campo debe llamarse `files` (FilesInterceptor). No fijar Content-Type a mano:
    // el navegador añade el boundary; sin él Multer no parsea el multipart.
    files.forEach((f) => formData.append('files', f, f.webkitRelativePath || f.name));
    return api.post(`/bandas/me/obras-partituras/${obraId}/archivos`, formData, {
      timeout: 300000,
    });
  },
  updateObraPartituraPagina: (obraId, paginaId, data) =>
    api.patch(`/bandas/me/obras-partituras/${obraId}/paginas/${paginaId}`, data),
  aplicarEtiquetasRangoObraPartitura: (obraId, data) =>
    api.patch(`/bandas/me/obras-partituras/${obraId}/paginas/rango`, data),
  reordenarPaginasObraPartitura: (obraId, data) =>
    api.post(`/bandas/me/obras-partituras/${obraId}/paginas/reordenar`, data),
  autoEtiquetarPaginasSugerencias: (obraId) =>
    api.post(`/bandas/me/obras-partituras/${obraId}/paginas/auto-etiquetar-sugerencias`, {}, { timeout: 300000 }),
  iniciarAutoEtiquetado: (obraId, body = { reusarSiActivo: true }) =>
    api.post(`/bandas/me/obras-partituras/${obraId}/auto-etiquetar`, body),
  getAutoEtiquetadoEstado: (obraId, jobId) =>
    api.get(`/bandas/me/obras-partituras/${obraId}/auto-etiquetar/${jobId}`),
  cancelarAutoEtiquetado: (obraId, jobId) =>
    api.post(`/bandas/me/obras-partituras/${obraId}/auto-etiquetar/${jobId}/cancelar`, {}),
  aplicarSugerenciaPagina: (obraId, paginaId, body) =>
    api.post(`/bandas/me/obras-partituras/${obraId}/paginas/${paginaId}/aplicar-sugerencia`, body),
  deleteObraPartituraPagina: (obraId, paginaId) =>
    api.delete(`/bandas/me/obras-partituras/${obraId}/paginas/${paginaId}`),
  dividirLoteObras: (obraId, body) =>
    api.post(`/bandas/me/obras-partituras/${obraId}/dividir-lote`, body),
  deleteObraPartitura: (obraId) => api.delete(`/bandas/me/obras-partituras/${obraId}`),
  // Verificación
  getVerificationStatus: () => api.get('/bandas/me/verification'),
  requestVerification: (data) => api.post('/bandas/me/verification', data),
  // Micrositio
  getBySlug: (slug) => api.get(`/bandas/by-slug/${slug}`),
  checkSlug: (slug, excludeBandaId) => api.get(`/bandas/check-slug/${slug}`, { params: { excludeBandaId } }),
  updateSlug: (slug) => api.patch('/bandas/me/slug', { slug }),
  updateMicrositeConfig: (config) => api.patch('/bandas/me/microsite-config', { micrositeConfig: config }),
  uploadMicrositeDirectivaFoto: (memberId, file, previousPhotoUrl) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('memberId', memberId);
    if (previousPhotoUrl) formData.append('previousPhotoUrl', previousPhotoUrl);
    return api.post('/bandas/me/microsite/directiva-foto', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteMicrositeDirectivaFoto: (photoUrl) =>
    api.delete('/bandas/me/microsite/directiva-foto', { data: { photoUrl } }),
  uploadMicrositePatrocinadorLogo: (sponsorId, file, previousLogoUrl) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sponsorId', sponsorId);
    if (previousLogoUrl) formData.append('previousLogoUrl', previousLogoUrl);
    return api.post('/bandas/me/microsite/patrocinador-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteMicrositePatrocinadorLogo: (logoUrl) =>
    api.delete('/bandas/me/microsite/patrocinador-logo', { data: { logoUrl } }),
  uploadMicrositeNoticiaImagen: (noticiaId, file, previousImageUrl) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('noticiaId', noticiaId);
    if (previousImageUrl) formData.append('previousImageUrl', previousImageUrl);
    return api.post('/bandas/me/microsite/noticia-imagen', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteMicrositeNoticiaImagen: (imageUrl) =>
    api.delete('/bandas/me/microsite/noticia-imagen', { data: { imageUrl } }),
  uploadMicrositeNoticiaMedia: (noticiaId, mediaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('noticiaId', noticiaId);
    formData.append('mediaId', mediaId);
    return api.post('/bandas/me/microsite/noticia-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteMicrositeNoticiaMedia: (mediaUrl) =>
    api.delete('/bandas/me/microsite/noticia-media', { data: { mediaUrl } }),
  getMicrositePreview: () => api.get('/bandas/me/microsite-preview'),
  publishMicrosite: () => api.post('/bandas/me/microsite/publish'),
  listMicrositePublicaciones: () => api.get('/bandas/me/microsite/publicaciones'),
  restoreMicrositePublicacionDraft: (publicacionId) =>
    api.post(`/bandas/me/microsite/publicaciones/${encodeURIComponent(publicacionId)}/restaurar-borrador`),
};

// Cofradías API
export const cofradiasAPI = {
  getAll: (params) => api.get('/cofradias', { params }),
  getById: (id) => api.get(`/cofradias/${id}`),
  getMyProfile: () => api.get('/cofradias/me/profile'),
  update: (data) => api.patch('/cofradias/me', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cofradias/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFondo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cofradias/me/fondo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadPhotos: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/cofradias/me/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (photoUrl) => api.delete('/cofradias/me/photos', { data: { photoUrl } }),
  // Verificación
  getVerificationStatus: () => api.get('/cofradias/me/verification'),
  requestVerification: (data) => api.post('/cofradias/me/verification', data),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getPendingVerifications: () => api.get('/admin/verifications/pending'),
  getAllVerifications: (params) => api.get('/admin/verifications', { params }),
  searchByCode: (code) => api.get('/admin/verifications/search', { params: { code } }),
  getVerificationDetails: (id) => api.get(`/admin/verifications/${id}`),
  processVerification: (id, data) => api.post(`/admin/verifications/${id}/process`, data),
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getUserProfile: (id, userType) => api.get(`/admin/users/${id}/profile`, { params: { userType } }),
  // Usage / coste de APIs externas
  getUsageSummary: (params) => api.get('/admin/usage/summary', { params }),
  getUsageTop: (params) => api.get('/admin/usage/top', { params }),
  getUsageForUser: (userId, params) => api.get(`/admin/usage/user/${userId}`, { params }),
  // Planes / control de costes
  getPlanCatalog: () => api.get('/admin/plans/catalog'),
  getPlanConsumption: () => api.get('/admin/plans/consumption'),
  patchPlanCatalog: (tier, data) => api.patch(`/admin/plans/catalog/${tier}`, data),
  resetPlanCatalog: (tier) => api.post(`/admin/plans/catalog/${tier}/reset`),
  getUsageObrasForBanda: (bandaId, params) =>
    api.get(`/admin/usage/banda/${bandaId}/obras`, { params }),
  setUserPlan: (userId, tier) => api.patch(`/admin/users/${userId}/plan`, { tier }),
  setUserFeatureOverrides: (userId, overrides) => api.patch(`/admin/users/${userId}/feature-overrides`, { overrides }),
  setUserBudgetOverride: (userId, budgetEur) => api.patch(`/admin/users/${userId}/budget-override`, { budgetEur }),
};

// Disponibilidad API
export const disponibilidadAPI = {
  getAll: (params) => api.get('/disponibilidad', { params }),
  create: (data) => api.post('/disponibilidad', data),
  createBulk: (data) => api.post('/disponibilidad/bulk', data),
  update: (fecha, data) => api.patch(`/disponibilidad/${fecha}`, data),
  delete: (fecha) => api.delete(`/disponibilidad/${fecha}`),
  deleteBulk: (fechas) => api.delete('/disponibilidad/bulk', { data: { fechas } }),
  togglePublicBulk: (data) => api.patch('/disponibilidad/toggle-public/bulk', data),
  togglePublicAll: (isPublic) => api.patch('/disponibilidad/toggle-public/all', { isPublic }),
};

// Eventos API
export const eventosAPI = {
  getAll: (params) => api.get('/eventos', { params }),
  getById: (id) => api.get(`/eventos/${id}`),
  create: (data) => {
    const promise = api.post('/eventos', data);
    promise.then(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        trackEventCreated(user?.userType);
      } catch { /* ignore */ }
    }).catch(() => { /* ignore */ });
    return promise;
  },
  update: (id, data) => api.patch(`/eventos/${id}`, data),
  delete: (id, cancelReason) => api.delete(`/eventos/${id}`, { data: { cancelReason } }),
  // Media (cartel/galería)
  uploadCartel: (eventoId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/eventos/${eventoId}/cartel`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteCartel: (eventoId) => api.delete(`/eventos/${eventoId}/cartel`),
  uploadFotos: (eventoId, files) => {
    const fd = new FormData();
    (files || []).forEach((f) => fd.append('files', f));
    return api.post(`/eventos/${eventoId}/fotos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteFoto: (eventoId, url) => api.delete(`/eventos/${eventoId}/fotos`, { data: { url } }),
  // Shortlist
  addToShortlist: (eventoId, bandaId, opts = {}) => {
    const payload =
      opts && typeof opts.openedChatWithMessage === 'boolean'
        ? { openedChatWithMessage: opts.openedChatWithMessage }
        : {};
    return api.post(`/eventos/${eventoId}/shortlist/${bandaId}`, payload);
  },
  removeFromShortlist: (eventoId, bandaId) => api.delete(`/eventos/${eventoId}/shortlist/${bandaId}`),
  confirmBanda: (eventoId, bandaId) => api.post(`/eventos/${eventoId}/confirmar/${bandaId}`),
  // Bandas
  showInterest: (eventoId, data) => api.post(`/eventos/${eventoId}/mostrar-interes`, data),
  rejectEvent: (eventoId) => api.post(`/eventos/${eventoId}/rechazar`),
};

// Mensajes API
export const mensajesAPI = {
  getConversaciones: () => api.get('/conversaciones'),
  getConversacion: (id) => api.get(`/conversaciones/${id}`),
  getMensajes: (conversacionId, params) => api.get(`/conversaciones/${conversacionId}/mensajes`, { params }),
  createConversacion: (otherUserId, eventoId) => {
    const promise = api.post('/conversaciones', { otherUserId, eventoId });
    promise.then(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        trackConversationStarted(user?.userType);
      } catch { /* ignore */ }
    }).catch(() => { /* ignore tracking errors */ });
    return promise;
  },
  sendMensaje: (conversacionId, content) => api.post(`/conversaciones/${conversacionId}/mensajes`, { content }),
  markAsRead: (conversacionId) => api.patch(`/conversaciones/${conversacionId}/read`),
};

// Reseñas API
export const resenasAPI = {
  getRecibidas: () => api.get('/resenas/recibidas'),
  getEscritas: () => api.get('/resenas/escritas'),
  getEventosPendientes: () => api.get('/resenas/eventos-pendientes'),
  create: (data) => api.post('/resenas', data),
  togglePublish: (resenaId) => api.patch(`/resenas/${resenaId}/toggle-publish`),
  delete: (resenaId) => api.delete(`/resenas/${resenaId}`),
  getPublicasBanda: (bandaId) => api.get(`/resenas/banda/${bandaId}`),
  hasReviewForEvent: (eventoId) => api.get(`/resenas/evento/${eventoId}/exists`),
};

// Avales API
export const avalesAPI = {
  // Banda endpoints
  solicitar: (data) => api.post('/avales/solicitar', data),
  getRecibidos: () => api.get('/avales/recibidos'),
  getPendientes: () => api.get('/avales/pendientes'),
  getSolicitudesEnviadas: () => api.get('/avales/solicitudes'),
  aceptar: (avalId) => api.patch(`/avales/${avalId}/aceptar`),
  rechazar: (avalId) => api.patch(`/avales/${avalId}/rechazar`),
  // Cofradia endpoints
  otorgar: (bandaId, texto) => api.post(`/avales/${bandaId}`, { bandaId, texto }),
  getOtorgados: () => api.get('/avales/otorgados'),
  revocar: (avalId) => api.delete(`/avales/${avalId}`),
  // Token-based endpoints (from email link)
  verificarToken: (token) => api.get(`/avales/verificar-token/${token}`),
  completarConToken: (token, texto) => api.post(`/avales/completar/${token}`, { texto }),
  // Public endpoints
  getPublicosBanda: (bandaId) => api.get(`/avales/banda/${bandaId}`),
  countBanda: (bandaId) => api.get(`/avales/banda/${bandaId}/count`),
};

// Notificaciones API
export const notificacionesAPI = {
  getAll: (params) => api.get('/notificaciones', { params }),
  getUnreadCount: () => api.get('/notificaciones/unread-count'),
  getLoginAvisos: () => api.get('/notificaciones/login-avisos'),
  dismissLoginAviso: (id) => api.post('/notificaciones/login-avisos/dismiss', { id }),
  markAsRead: (id) => api.patch(`/notificaciones/${id}/read`),
  markAllAsRead: () => api.patch('/notificaciones/read-all'),
  delete: (id) => api.delete(`/notificaciones/${id}`),
};

// Catálogo API
export const catalogoAPI = {
  getMarchas: (params) => api.get('/catalogo/marchas', { params }),
  searchMarchas: (q, limit = 15) => api.get('/catalogo/marchas/search', { params: { q, limit } }),
  searchAutores: (q, limit = 10) => api.get('/catalogo/autores/search', { params: { q, limit } }),
  getAutores: () => api.get('/catalogo/autores'),
  getTiposObra: () => api.get('/catalogo/tipos-obra'),
  getProvincias: () => api.get('/catalogo/provincias'),
  /** Tipos de entidad (perfil cofradía / promotor): id, codigo, nombre, variantePerfil */
  getTiposEntidadCadencya: () => api.get('/catalogo/tipos-entidad-cadencya'),
  getTiposFormacionActuacion: () => api.get('/catalogo/tipos-formacion-actuacion'),
};

// Acuerdos API
export const acuerdosAPI = {
  getByConversacion: (conversacionId) => api.get(`/acuerdos/conversacion/${conversacionId}`),
  getActive: (conversacionId) => api.get(`/acuerdos/conversacion/${conversacionId}/active`),
  create: (data) => api.post('/acuerdos', data),
  respond: (acuerdoId, data) => api.patch(`/acuerdos/${acuerdoId}/respond`, data),
  cancel: (acuerdoId) => api.patch(`/acuerdos/${acuerdoId}/cancel`),
  rescind: (acuerdoId, reason) => api.patch(`/acuerdos/${acuerdoId}/rescind`, { reason }),
  uploadAdjuntos: (acuerdoId, files, opts = {}) => {
    const fd = new FormData();
    (files || []).forEach((f) => fd.append('files', f));
    fd.append('aceptaDisclaimer', String(Boolean(opts.aceptaDisclaimer)));
    return api.post(`/acuerdos/${encodeURIComponent(acuerdoId)}/adjuntos`, fd);
  },
  deleteAdjunto: (acuerdoId, url) =>
    api.delete(`/acuerdos/${encodeURIComponent(acuerdoId)}/adjuntos`, { data: { url } }),
};

// Instrumentos API
export const instrumentosAPI = {
  /** @param {string|{ familia?: string, tipoGrupo?: string, tipoAgrupacionCodigo?: string }} [opts] */
  getAll: (opts) => {
    if (opts == null) return api.get('/instrumentos');
    if (typeof opts === 'string') return api.get('/instrumentos', { params: { familia: opts } });
    return api.get('/instrumentos', { params: opts });
  },
  getTiposAgrupacion: () => api.get('/instrumentos/tipos-agrupacion'),
  getPersonalizados: () => api.get('/instrumentos/personalizados'),
  createPersonalizado: (data) => api.post('/instrumentos/personalizados', data),
  deletePersonalizado: (id) => api.delete(`/instrumentos/personalizados/${id}`),
};

// Musicos API
export const musicosAPI = {
  getAll: () => api.get('/musicos'),
  create: (data) => api.post('/musicos', data),
  update: (id, data) => api.patch(`/musicos/${id}`, data),
  delete: (id) => api.delete(`/musicos/${id}`),
  /** Multipart: el servidor redimensiona y comprime la imagen (JPEG). */
  uploadFoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/musicos/${encodeURIComponent(id)}/foto`, fd);
  },
  deleteFoto: (id) => api.delete(`/musicos/${id}/foto`),
  getGrupos: () => api.get('/musicos/grupos'),
  createGrupo: (data) => api.post('/musicos/grupos', data),
  updateGrupo: (id, data) => api.patch(`/musicos/grupos/${id}`, data),
  deleteGrupo: (id) => api.delete(`/musicos/grupos/${id}`),
};

// Formaciones API
export const formacionesAPI = {
  getPlantillas: () => api.get('/formaciones/plantillas'),
  getPlantilla: (id) => api.get(`/formaciones/plantillas/${id}`),
  createPlantilla: (data) => api.post('/formaciones/plantillas', data),
  updatePlantilla: (id, data) => api.patch(`/formaciones/plantillas/${id}`, data),
  deletePlantilla: (id) => api.delete(`/formaciones/plantillas/${id}`),
  savePosiciones: (id, posiciones) => api.post(`/formaciones/plantillas/${id}/posiciones`, { posiciones }),
  autoSuggest: (plantillaId) => api.post(`/formaciones/plantillas/${plantillaId}/auto-suggest`),
  getFormacionesEvento: () => api.get('/formaciones/eventos'),
  getFormacionEvento: (id) => api.get(`/formaciones/eventos/${id}`),
  createFormacionEvento: (data) => api.post('/formaciones/eventos', data),
  deleteFormacionEvento: (id) => api.delete(`/formaciones/eventos/${id}`),
  saveAsignaciones: (id, asignaciones) => api.post(`/formaciones/eventos/${id}/asignaciones`, { asignaciones }),
};

// Actuaciones API
export const actuacionesAPI = {
  getAll: () => api.get('/actuaciones'),
  getById: (id) => api.get(`/actuaciones/${id}`),
  create: (data) => api.post('/actuaciones', data),
  update: (id, data) => api.patch(`/actuaciones/${id}`, data),
  delete: (id) => api.delete(`/actuaciones/${id}`),
  crearEncuesta: (id, data) => api.post(`/actuaciones/${id}/encuesta`, data),
  cerrarEncuesta: (id) => api.patch(`/actuaciones/${id}/encuesta`, { estado: 'CERRADA' }),
  reabrirEncuesta: (id) => api.patch(`/actuaciones/${id}/encuesta/reabrir`),
  getResultados: (id) => api.get(`/actuaciones/${id}/encuesta/resultados`),
  updateRespuesta: (id, respuestaId, data) => api.patch(`/actuaciones/${id}/encuesta/respuestas/${respuestaId}`, data),
  editarRespuesta: (id, respuestaId, data) => api.patch(`/actuaciones/${id}/encuesta/respuestas/${respuestaId}`, data),
  getNecesidades: (id) => api.get(`/actuaciones/${id}/necesidades`),
  getRefuerzos: (id) => api.get(`/actuaciones/${id}/refuerzos`),
  addRefuerzo: (id, data) => api.post(`/actuaciones/${id}/refuerzos`, data),
  updateRefuerzo: (id, refId, data) => api.patch(`/actuaciones/${id}/refuerzos/${refId}`, data),
  deleteRefuerzo: (id, refId) => api.delete(`/actuaciones/${id}/refuerzos/${refId}`),
  getFormacionConAsistencia: (id) => api.get(`/actuaciones/${id}/formacion`),
  getVacantes: (id) => api.get(`/actuaciones/${id}/vacantes`),
  registrarContacto: (id, refId) => api.patch(`/actuaciones/${id}/refuerzos/${refId}/contactar`),
  generarFormacion: (id) => api.post(`/actuaciones/${id}/generar-formacion`),
  /** Imagen o cartel (JPEG, PNG o WebP; máx. 8 MB en servidor). */
  uploadCartel: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/actuaciones/${id}/cartel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  removeCartel: (id) => api.delete(`/actuaciones/${id}/cartel`),
  getBandaAvatarDataUrl: (id) => api.get(`/actuaciones/${id}/banda-avatar-data-url`),
  getBandaAvatarFile: (id) => api.get(`/actuaciones/${id}/banda-avatar`, { responseType: 'blob' }),
  getCartelBytes: (id) => api.get(`/actuaciones/${id}/cartel-bytes`, { responseType: 'blob' }),
};

// Contactos Agenda API
export const contactosAPI = {
  // Pestañas personalizadas
  getPestanas: () => api.get('/contactos/pestanas'),
  createPestana: (data) => api.post('/contactos/pestanas', data),
  updatePestana: (id, data) => api.patch(`/contactos/pestanas/${id}`, data),
  deletePestana: (id) => api.delete(`/contactos/pestanas/${id}`),
  // Contactos
  getContactos: (params) => api.get('/contactos', { params }),
  getContacto: (id) => api.get(`/contactos/${id}`),
  createContacto: (data) => api.post('/contactos', data),
  updateContacto: (id, data) => api.patch(`/contactos/${id}`, data),
  deleteContacto: (id) => api.delete(`/contactos/${id}`),
};

export const supportAPI = {
  createTicket: ({ email, asunto, mensaje }) => api.post('/support/ticket', { email, asunto, mensaje }),
};

// ─── Ensayos ────────────────────────────────────────────────────────────────
export const ensayosAPI = {
  // Grupos
  getGrupos: () => api.get('/ensayos/grupos'),
  getGrupo: (id) => api.get(`/ensayos/grupos/${id}`),
  createGrupo: (data) => api.post('/ensayos/grupos', data),
  updateGrupo: (id, data) => api.patch(`/ensayos/grupos/${id}`, data),
  deleteGrupo: (id) => api.delete(`/ensayos/grupos/${id}`),

  // Ensayos individuales
  getAll: (params = {}) => api.get('/ensayos', { params }),
  getOne: (id) => api.get(`/ensayos/${id}`),
  create: (data) => api.post('/ensayos', data),
  createRecurrentes: (data) => api.post('/ensayos/recurrentes', data),
  asignarGrupo: (data) => api.post('/ensayos/asignar-grupo', data),
  update: (id, data) => api.patch(`/ensayos/${id}`, data),
  importRepertorioActuacion: (id) => api.post(`/ensayos/${id}/importar-repertorio-actuacion`),
  remove: (id) => api.delete(`/ensayos/${id}`),

  // Convocados
  updateConvocados: (id, musicoIds) => api.patch(`/ensayos/${id}/convocados`, { musicoIds }),

  // Asistencia
  getAsistencia: (id) => api.get(`/ensayos/${id}/asistencia`),
  setAsistencia: (id, musicoId, data) => api.patch(`/ensayos/${id}/asistencia/${musicoId}`, data),
  bulkSetAsistencia: (id, asistencias) => api.patch(`/ensayos/${id}/asistencia`, { asistencias }),

  // Estadísticas globales
  getEstadisticas: (params = {}) => api.get('/ensayos/estadisticas', { params }),

  // Integración con Actuaciones
  getBajaAsistencia: (actuacionId) => api.get(`/ensayos/baja-asistencia/${actuacionId}`),

  // Notificaciones por email
  enviarNotificaciones: (horasAntelacion = 24) => api.post('/ensayos/notificaciones/enviar', { horasAntelacion }),
  getNotificaciones: (ensayoId) => api.get(`/ensayos/${ensayoId}/notificaciones`),

  // Vacaciones
  getVacaciones: () => api.get('/ensayos/vacaciones'),
  createVacacion: (data) => api.post('/ensayos/vacaciones', data),
  updateVacacion: (id, data) => api.patch(`/ensayos/vacaciones/${id}`, data),
  deleteVacacion: (id) => api.delete(`/ensayos/vacaciones/${id}`),

  // Sedes
  getSedes: () => api.get('/ensayos/sedes'),
  createSede: (data) => api.post('/ensayos/sedes', data),
  updateSede: (id, data) => api.patch(`/ensayos/sedes/${id}`, data),
  deleteSede: (id) => api.delete(`/ensayos/sedes/${id}`),
  uploadSedeImagen: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/ensayos/sedes/${id}/imagen`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeSedeImagen: (id) => api.delete(`/ensayos/sedes/${id}/imagen`),
};

export const encuestaPublicaAPI = {
  get: (token) => api.get(`/encuesta/${token}`),
  respond: (token, data) => api.post(`/encuesta/${token}`, data),
};

/** Encuesta de asistencia compartida al grupo (identificar músico → token individual). */
export const encuestaGrupoAPI = {
  getMeta: (shareToken) => publicApi.get(`/encuesta-grupo/${encodeURIComponent(shareToken)}`),
  elegir: (shareToken, musicoId) =>
    publicApi.post(`/encuesta-grupo/${encodeURIComponent(shareToken)}/elegir`, { musicoId }),
};

/** Métricas compartibles sin sesión (JWT en query). */
export const publicMetricasAPI = {
  getResumen: (t) => publicApi.get('/public/metricas/resumen', { params: { t } }),
};

export const documentosBandaPublicAPI = {
  meta: (token) => publicApi.get(`/public/documentos-banda/${encodeURIComponent(token)}/meta`),
  unlock: (token, body) =>
    publicApi.post(`/public/documentos-banda/${encodeURIComponent(token)}/unlock`, body ?? {}),
  listDocumentos: (token, accessToken) =>
    publicApi.get(`/public/documentos-banda/${encodeURIComponent(token)}/documentos`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  getDocumentoBlob: (token, accessToken, documentoId) =>
    publicApi.get(
      `/public/documentos-banda/${encodeURIComponent(token)}/documentos/${encodeURIComponent(documentoId)}/archivo`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: 'blob',
      },
    ),
  /** Blob con Content-Type de respuesta (p. ej. abrir PDF en pestaña). */
  fetchDocumentoArchivoBlob: async (token, accessToken, documentoId) => {
    const res = await publicApi.get(
      `/public/documentos-banda/${encodeURIComponent(token)}/documentos/${encodeURIComponent(documentoId)}/archivo`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: 'blob',
      },
    );
    const mime =
      res.headers['content-type']?.split(';')[0]?.trim() || 'application/octet-stream';
    return new Blob([res.data], { type: mime });
  },
};

export const repertorioCompartidoAPI = {
  meta: (token) => publicApi.get(`/public/repertorio-compartido/${encodeURIComponent(token)}/meta`),
  unlock: (token, body) =>
    publicApi.post(`/public/repertorio-compartido/${encodeURIComponent(token)}/unlock`, body ?? {}),
  getMusicos: (token, accessToken) =>
    publicApi.get(`/public/repertorio-compartido/${encodeURIComponent(token)}/musicos`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  getCarpetas: (token, accessToken) =>
    publicApi.get(`/public/repertorio-compartido/${encodeURIComponent(token)}/carpetas`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  getObrasEnCarpeta: (token, accessToken, carpetaId, params = {}) =>
    publicApi.get(
      `/public/repertorio-compartido/${encodeURIComponent(token)}/carpetas/${encodeURIComponent(carpetaId)}/obras`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
        ),
      },
    ),
  getCarpetaPdfBlob: (token, accessToken, carpetaId, params = {}) =>
    publicApi.get(
      `/public/repertorio-compartido/${encodeURIComponent(token)}/carpetas/${encodeURIComponent(carpetaId)}/pdf`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
        responseType: 'blob',
      },
    ),
  getCarpetaZipBlob: (token, accessToken, carpetaId, params = {}) =>
    publicApi.get(
      `/public/repertorio-compartido/${encodeURIComponent(token)}/carpetas/${encodeURIComponent(carpetaId)}/zip`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
        responseType: 'blob',
      },
    ),
  getObraDetalle: (token, accessToken, obraId, params = {}) =>
    publicApi.get(`/public/repertorio-compartido/${encodeURIComponent(token)}/obras/${encodeURIComponent(obraId)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
      ),
    }),
  getObraPdfBlob: (token, accessToken, obraId, params = {}) =>
    publicApi.get(
      `/public/repertorio-compartido/${encodeURIComponent(token)}/obras/${encodeURIComponent(obraId)}/pdf`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
        responseType: 'blob',
      },
    ),
  reportarIncidencia: (token, accessToken, body) =>
    publicApi.post(`/public/repertorio-compartido/${encodeURIComponent(token)}/incidencias`, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
};

// ── Encuestas / Consultas ──────────────────────────────────────────────────
export const encuestasConsultasAPI = {
  getAll: () => api.get('/encuestas-consultas'),
  getOne: (id) => api.get(`/encuestas-consultas/${id}`),
  create: (data) => api.post('/encuestas-consultas', data),
  update: (id, data) => api.patch(`/encuestas-consultas/${id}`, data),
  remove: (id) => api.delete(`/encuestas-consultas/${id}`),
  changeEstado: (id, estado) => api.patch(`/encuestas-consultas/${id}/estado`, { estado }),
  uploadImagenPregunta: (encuestaId, preguntaId, formData) =>
    api.post(`/encuestas-consultas/${encuestaId}/preguntas/${preguntaId}/imagenes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadImagenOpcion: (encuestaId, opcionId, formData) =>
    api.post(`/encuestas-consultas/${encuestaId}/opciones/${opcionId}/imagenes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteImagen: (imagenId) => api.delete(`/encuestas-consultas/imagenes/${imagenId}`),
  getResultados: (id) => api.get(`/encuestas-consultas/${id}/resultados`),
  getResultadosPdf: (id) => api.get(`/encuestas-consultas/${id}/resultados/pdf`, { responseType: 'blob' }),
};

export const encuestasPublicasAPI = {
  getMeta: (shareToken) => publicApi.get(`/encuestas-publicas/${shareToken}`),
  getMusicos: (shareToken) => publicApi.get(`/encuestas-publicas/${shareToken}/musicos`),
  verificar: (shareToken, convocadoId, password) =>
    publicApi.post(`/encuestas-publicas/${shareToken}/verificar`, { convocadoId, password }),
  getEncuesta: (convocadoToken) => publicApi.get(`/encuestas-publicas/responder/${convocadoToken}`),
  responder: (convocadoToken, respuestas) =>
    publicApi.post(`/encuestas-publicas/responder/${convocadoToken}`, { respuestas }),
};

export default api;


